import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'react-oidc-context'
import { fetchMe } from '@/api/auth'
import {
  checkHandleAvailability,
  deleteAvatar,
  fetchMySubscriptions,
  fetchPublicProfile,
  subscribeToCreator,
  unsubscribeFromCreator,
  updateMyProfile,
  uploadAvatar,
} from '@/api/users'
import type { HandleAvailability, Me, SubscribedCreator, UpdateProfileRequest } from '@/api/types'

export const USERS_QUERY_KEY = ['users'] as const

export const useMe = () => {
  const auth = useAuth()
  return useQuery({
    queryKey: [...USERS_QUERY_KEY, 'me'],
    queryFn: fetchMe,
    enabled: auth.isAuthenticated,
    staleTime: 60_000,
  })
}

export const usePublicProfile = (handle: string | undefined) =>
  useQuery({
    queryKey: [...USERS_QUERY_KEY, 'profile', handle],
    queryFn: () => fetchPublicProfile(handle as string),
    enabled: Boolean(handle),
    // Phase 8: hero thumbs in profile project lists are signed URLs; keep just under 1-hour TTL.
    staleTime: 50 * 60 * 1000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number })?.status
      if (status === 404) return false
      return failureCount < 3
    },
  })

export const useUpdateProfile = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: UpdateProfileRequest) => updateMyProfile(req),
    onSuccess: (data: Me) => {
      queryClient.setQueryData([...USERS_QUERY_KEY, 'me'], data)
      void queryClient.invalidateQueries({ queryKey: [...USERS_QUERY_KEY] })
    },
  })
}

export const useHandleAvailability = (handle: string | undefined) =>
  useQuery<HandleAvailability>({
    queryKey: [...USERS_QUERY_KEY, 'handle-availability', handle],
    queryFn: () => checkHandleAvailability(handle as string),
    enabled: Boolean(handle && handle.length >= 3),
    staleTime: 5_000,
  })

export const useMySubscriptions = () => {
  const auth = useAuth()
  return useQuery<SubscribedCreator[]>({
    queryKey: [...USERS_QUERY_KEY, 'subscriptions'],
    queryFn: fetchMySubscriptions,
    enabled: auth.isAuthenticated,
  })
}

/**
 * Phase 7 — upload a cropped Avatar blob and update every Avatar-bearing surface. The new
 * {@code avatarUrl} lands on Me, but the Avatar shows up on browse cards, profile views, doer
 * surfaces, and Subscriptions feed too — invalidate broadly. Cheap; the cache is small.
 */
export const useUploadAvatar = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (blob: Blob) => uploadAvatar(blob),
    onSuccess: (data: Me) => {
      queryClient.setQueryData([...USERS_QUERY_KEY, 'me'], data)
      void queryClient.invalidateQueries({ queryKey: [...USERS_QUERY_KEY] })
      // Avatar appears on browse cards / following surfaces too — invalidate those caches.
      void queryClient.invalidateQueries({ queryKey: ['browse'] })
      void queryClient.invalidateQueries({ queryKey: ['followings'] })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export const useDeleteAvatar = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => deleteAvatar(),
    onSuccess: (data: Me) => {
      queryClient.setQueryData([...USERS_QUERY_KEY, 'me'], data)
      void queryClient.invalidateQueries({ queryKey: [...USERS_QUERY_KEY] })
      void queryClient.invalidateQueries({ queryKey: ['browse'] })
      void queryClient.invalidateQueries({ queryKey: ['followings'] })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export const useToggleSubscription = (handle: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (subscribed: boolean) => {
      if (subscribed) {
        await subscribeToCreator(handle)
      } else {
        await unsubscribeFromCreator(handle)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...USERS_QUERY_KEY, 'profile', handle] })
      void queryClient.invalidateQueries({ queryKey: [...USERS_QUERY_KEY, 'subscriptions'] })
    },
  })
}
